import { useEffect } from 'react';
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';

import { UserProvider } from './UserProvider';

import AuthLayout from './layouts/Auth';
import MainLayout from './layouts/Main';

import AccountSettings from './routes/AccountSettings/AccountSettings';
import DeleteAccount from './routes/AccountSettings/DeleteAccount/DeleteAccount';
import ArtistPage from './routes/ArtistPage/ArtistPage';
import ChatPage from './routes/ChatPage/ChatPage';
import ClaimPage from './routes/ClaimPage/ClaimPage';
import InboxPage from './routes/InboxPage/InboxPage';
import JobsPage from './routes/JobsPage/JobsPage';
import ArtistsPage from './routes/ArtistsPage/ArtistsPage';
import LandingPage from './routes/LandingPage/LandingPage';
import LoginPage from './routes/LoginPage/LoginPage';
import LogoutPage from './routes/LogoutPage/LogoutPage';
import OnboardingPage from './routes/OnboardingPage/OnboardingPage';
import PasswordResetPage from './routes/PasswordResetPage/PasswordResetPage';
import PrivacyPage from './routes/PrivacyPage/PrivacyPage';
import RssFeed from './routes/RssFeed/RssFeed';
import RssFeeds from './routes/RssFeeds/RssFeeds';
import SignupPage from './routes/SignupPage/SignupPage';
import TermsPage from './routes/TermsPage/TermsPage';
import UserData from './routes/UserData/UserData';

import AdminRoute from './routes/AdminRoute';
import PrivateRoute from './routes/PrivateRoute';
import FacebookCandidates from './routes/FacebookCandidates/FacebookCandidates';

import ReactGA from "react-ga4";
import Clarity from '@microsoft/clarity';
import EmailResetPage from './routes/EmailResetPage/EmailResetPage';
import JobPage from './routes/JobPage/JobPage';
import WalletPage from './routes/WalletPage/WalletPage';

function StaticRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

if (process.env.NODE_ENV === "production") {
  ReactGA.initialize(process.env.REACT_APP_GOOGLE_MEASUREMENT_ID);

  const projectId = process.env.REACT_APP_CLARITY_ID;
  Clarity.init(projectId);
}

function GAListener() {
  const location = useLocation();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      // send_to scopes this to the main property only. Without it, gtag
      // broadcasts to every configured property, including the Wallet
      // property once WalletPage has registered it (see utils/analytics.js).
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
        send_to: process.env.REACT_APP_GOOGLE_MEASUREMENT_ID,
      });
    }
  }, [location]);

  return null;
}

function App() {
  return (
    <Router>
      <UserProvider>
        <GAListener />
        <Routes>
          {/* Public Routes */}
          <Route path="/logout"          element={<LogoutPage  />} />

          <Route element={<AuthLayout />}>
            <Route path="/login"               element={<LoginPage         />} />
            <Route path="/signup"              element={<SignupPage        />} />
            <Route path="/password-reset"      element={<PasswordResetPage />} />
            <Route path="/email-reset"         element={<EmailResetPage    />} />
            <Route path="/claim/:access_token" element={<ClaimPage         />} />
          </Route>

          <Route path="/a/rvega/marketing-agent" element={<StaticRedirect to="/rvega.html" />} />
          <Route path="/a/nomadfunk/EPK" element={<StaticRedirect to="/nomad_funk_epk.html" />} />
          <Route path="/a/next100/play" element={<StaticRedirect to="/next-100/index.html" />} />

          <Route element={<MainLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/a/:display_name" element={<ArtistPage  />} />
            <Route path="/p/:display_name" element={<ArtistPage  />} />
            <Route path="/privacy"         element={<PrivacyPage />} />
            <Route path="/terms"           element={<TermsPage   />} />
            <Route path="/jobs/:tab?" element={<JobsPage />} />
            <Route path="/artists" element={<ArtistsPage />} />
            <Route path="/job/:job_id" element={ <JobPage /> } />
            <Route path="/wallet"     element={<WalletPage />} />
          </Route>

          {/* Private Routes */}
          <Route element={<PrivateRoute />}>
            <Route path="/onboarding"                  element={<OnboardingPage />} />
            <Route path="/onboarding/:onboarding_step" element={<OnboardingPage />} />

            <Route element={<MainLayout />}>
              <Route path="/home"                            element={<ChatPage       />} />
              <Route path="/chat"                            element={<ChatPage       />} />
              <Route path="/account-settings"                element={<AccountSettings />} />
              <Route path="/account-settings/delete-account" element={<DeleteAccount   />} />
              <Route path="/inbox"                           element={<InboxPage       />} />
              <Route path="/inbox/:display_name"             element={<InboxPage       />} />
            </Route>
          </Route>

          {/* Admin Routes */}
          <Route element={<AdminRoute />}>
            <Route element={<MainLayout />}>          
              <Route path="/user-data"             element={<UserData           />} />
              <Route path="/rss-feeds"             element={<RssFeeds           />} />
              <Route path="/rss-feed/:rss_feed_id" element={<RssFeed            />} />
              <Route path="/facebook-candidates"   element={<FacebookCandidates />} />
            </Route>
          </Route>
        </Routes>
      </UserProvider>
    </Router>
  );
}

export default App;